import type { V1GameEngine } from "./v1/engine";

interface CloudUser {
  id: string;
  username: string;
}

interface CloudSaveRecord {
  revision: number;
  contentHash: string;
  saveJson: string;
  updatedAt: string;
}

interface ApiPayload {
  error?: string;
  user?: CloudUser;
  save?: CloudSaveRecord | null;
}

interface CloudAccountConfig {
  apiBase?: string;
  gameId: string;
  gameName: string;
}

const GUEST_SESSION_KEY = "jintu-rpg-guest-session";

const requestJson = async (
  path: string,
  init?: RequestInit,
): Promise<ApiPayload> => {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const payload = await response.json().catch(() => ({})) as ApiPayload;
  if (!response.ok) throw new Error(payload.error ?? `请求失败（${response.status}）`);
  return payload;
};

const formatCloudTime = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export class CloudAccountController {
  private readonly launcher: HTMLButtonElement;
  private readonly backdrop: HTMLDivElement;
  private readonly title: HTMLElement;
  private readonly message: HTMLElement;
  private readonly authForm: HTMLFormElement;
  private readonly accountView: HTMLElement;
  private readonly usernameInput: HTMLInputElement;
  private readonly passwordInput: HTMLInputElement;
  private readonly submitButton: HTMLButtonElement;
  private readonly modeButton: HTMLButtonElement;
  private readonly guestButton: HTMLButtonElement;
  private readonly accountName: HTMLElement;
  private readonly cloudDescription: HTMLElement;
  private readonly uploadButton: HTMLButtonElement;
  private readonly downloadButton: HTMLButtonElement;
  private mode: "login" | "register" = "login";
  private user: CloudUser | null = null;
  private cloudSave: CloudSaveRecord | null = null;

  constructor(
    private readonly engine: V1GameEngine,
    private readonly config: CloudAccountConfig,
  ) {
    this.launcher = document.createElement("button");
    this.launcher.className = "cloud-account-launcher";
    this.launcher.type = "button";
    this.launcher.textContent = "登录 / 注册";
    this.launcher.setAttribute("aria-label", "登录、注册或管理云存档");

    this.backdrop = document.createElement("div");
    this.backdrop.className = "cloud-account-backdrop";
    this.backdrop.hidden = true;
    this.backdrop.innerHTML = `
      <section class="cloud-account-panel" role="dialog" aria-modal="true" aria-labelledby="cloud-account-title">
        <button type="button" class="cloud-account-close" aria-label="关闭">×</button>
        <h2 id="cloud-account-title">账号与云存档</h2>
        <p class="cloud-account-message" aria-live="polite"></p>
        <form class="cloud-auth-form">
          <p class="cloud-auth-intro">登录后共用玩家账号；每个游戏版本使用独立云存档。</p>
          <label>用户名<input name="username" autocomplete="username" minlength="3" maxlength="20" required /></label>
          <label>密码<input name="password" type="password" autocomplete="current-password" minlength="8" maxlength="72" required /></label>
          <button type="submit" class="cloud-primary">登录</button>
          <button type="button" class="cloud-mode">没有账号？注册</button>
          <button type="button" class="cloud-guest">游客试玩 · 仅保存在本机</button>
          <small>第一版不绑定邮箱；忘记密码后暂时无法自动找回。</small>
        </form>
        <div class="cloud-account-view" hidden>
          <p class="cloud-account-name"></p>
          <p class="cloud-save-description"></p>
          <div class="cloud-account-actions">
            <button type="button" class="cloud-primary cloud-upload">上传本地存档</button>
            <button type="button" class="cloud-download">读取云存档</button>
            <button type="button" class="cloud-logout">退出登录</button>
          </div>
        </div>
      </section>
    `;

    const query = <T extends Element>(selector: string): T => {
      const element = this.backdrop.querySelector<T>(selector);
      if (!element) throw new Error(`账号界面缺少元素：${selector}`);
      return element;
    };
    this.title = query("#cloud-account-title");
    this.message = query(".cloud-account-message");
    this.authForm = query(".cloud-auth-form");
    this.accountView = query(".cloud-account-view");
    this.usernameInput = query('input[name="username"]');
    this.passwordInput = query('input[name="password"]');
    this.submitButton = query('.cloud-auth-form button[type="submit"]');
    this.modeButton = query(".cloud-mode");
    this.guestButton = query(".cloud-guest");
    this.accountName = query(".cloud-account-name");
    this.cloudDescription = query(".cloud-save-description");
    this.uploadButton = query(".cloud-upload");
    this.downloadButton = query(".cloud-download");

    document.body.append(this.launcher, this.backdrop);
    this.bindEvents(query(".cloud-account-close"), query(".cloud-logout"));
    this.engine.setCloudSaveHandler((saveJson) => this.uploadSave(saveJson, true));
  }

  async initialize(): Promise<void> {
    try {
      const payload = await this.request("/api/auth/me");
      this.user = payload.user ?? null;
      if (this.user) await this.refreshCloudSave();
    } catch {
      this.user = null;
    }
    this.render();
    if (!this.user && !this.guestSessionSelected()) this.open();
  }

  dispose(): void {
    this.engine.setCloudSaveHandler(null);
    this.launcher.remove();
    this.backdrop.remove();
  }

  private bindEvents(closeButton: HTMLButtonElement, logoutButton: HTMLButtonElement): void {
    this.launcher.addEventListener("click", () => this.open());
    closeButton.addEventListener("click", () => this.close());
    this.backdrop.addEventListener("click", (event) => {
      if (event.target === this.backdrop) this.close();
    });
    this.authForm.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.submitAuth();
    });
    this.modeButton.addEventListener("click", () => {
      this.mode = this.mode === "login" ? "register" : "login";
      this.passwordInput.value = "";
      this.message.textContent = "";
      this.render();
    });
    this.guestButton.addEventListener("click", () => {
      try {
        window.sessionStorage.setItem(GUEST_SESSION_KEY, "1");
      } catch {
        // 无 sessionStorage 时仍允许本次关闭并使用本地存档。
      }
      this.close();
    });
    this.uploadButton.addEventListener("click", () => {
      const local = this.engine.localSaveJson();
      if (!local) {
        this.setMessage("当前没有本地存档，请先在游戏中点击保存。", true);
        return;
      }
      if (this.cloudSave && !window.confirm("这会用当前本地进度覆盖云存档，确定继续吗？")) return;
      void this.uploadSave(local, false);
    });
    this.downloadButton.addEventListener("click", () => {
      if (!this.cloudSave) return;
      if (this.engine.hasSave() && !window.confirm("这会用云端进度覆盖当前本地存档，确定继续吗？")) return;
      const loaded = this.engine.importCloudSaveJson(this.cloudSave.saveJson);
      this.setMessage(loaded ? "云存档已读取。" : "云存档与当前游戏版本不兼容。", !loaded);
      if (loaded) this.close();
    });
    logoutButton.addEventListener("click", () => void this.logout());
  }

  private open(): void {
    this.backdrop.hidden = false;
    this.render();
    if (!this.user) this.usernameInput.focus();
  }

  private close(): void {
    this.backdrop.hidden = true;
  }

  private guestSessionSelected(): boolean {
    try {
      return window.sessionStorage.getItem(GUEST_SESSION_KEY) === "1";
    } catch {
      return false;
    }
  }

  private async submitAuth(): Promise<void> {
    const username = this.usernameInput.value.trim();
    const password = this.passwordInput.value;
    this.setBusy(true);
    try {
      const payload = await this.request(`/api/auth/${this.mode}`, {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      this.user = payload.user ?? null;
      this.passwordInput.value = "";
      await this.refreshCloudSave();
      this.setMessage(this.mode === "register" ? "注册成功，已经登录。" : "登录成功。", false);
    } catch (error) {
      this.setMessage(error instanceof Error ? error.message : "登录失败。", true);
    } finally {
      this.setBusy(false);
      this.render();
    }
  }

  private async logout(): Promise<void> {
    try {
      await this.request("/api/auth/logout", { method: "POST", body: "{}" });
    } finally {
      this.user = null;
      this.cloudSave = null;
      this.setMessage("已退出登录，本地存档仍然保留。", false);
      this.render();
    }
  }

  private async refreshCloudSave(): Promise<void> {
    if (!this.user) return;
    const payload = await this.request(this.savePath());
    this.cloudSave = payload.save ?? null;
  }

  private async uploadSave(saveJson: string, silent: boolean): Promise<void> {
    if (!this.user) return;
    try {
      const payload = await this.request(this.savePath(), {
        method: "PUT",
        body: JSON.stringify({
          revision: this.cloudSave?.revision ?? null,
          saveJson,
        }),
      });
      this.cloudSave = payload.save ?? null;
      if (!silent) this.setMessage("本地进度已上传到云端。", false);
      this.render();
    } catch (error) {
      if (!silent) this.setMessage(error instanceof Error ? error.message : "云端保存失败。", true);
      await this.refreshCloudSave().catch(() => undefined);
      this.render();
    }
  }

  private setBusy(busy: boolean): void {
    this.submitButton.disabled = busy;
    this.submitButton.textContent = busy ? "处理中…" : this.mode === "login" ? "登录" : "注册并登录";
  }

  private request(path: string, init?: RequestInit): Promise<ApiPayload> {
    const base = this.config.apiBase?.replace(/\/$/u, "") ?? "";
    return requestJson(`${base}${path}`, init);
  }

  private savePath(): string {
    return `/api/games/${encodeURIComponent(this.config.gameId)}/save`;
  }

  private setMessage(message: string, error: boolean): void {
    this.message.textContent = message;
    this.message.classList.toggle("is-error", error);
  }

  private render(): void {
    const loggedIn = this.user !== null;
    this.launcher.textContent = loggedIn ? `玩家：${this.user!.username}` : "登录 / 注册";
    this.title.textContent = loggedIn ? "账号与云存档" : this.mode === "login" ? "登录" : "注册账号";
    this.authForm.hidden = loggedIn;
    this.accountView.hidden = !loggedIn;
    this.submitButton.textContent = this.mode === "login" ? "登录" : "注册并登录";
    this.modeButton.textContent = this.mode === "login" ? "没有账号？注册" : "已有账号？登录";
    this.passwordInput.autocomplete = this.mode === "login" ? "current-password" : "new-password";
    if (!this.user) return;
    this.accountName.textContent = `当前账号：${this.user.username}`;
    this.cloudDescription.textContent = this.cloudSave
      ? `${this.config.gameName}云存档：版本 ${this.cloudSave.revision} · ${formatCloudTime(this.cloudSave.updatedAt)}`
      : this.engine.hasSave()
        ? `${this.config.gameName}云端暂无存档，可以上传当前本地进度。`
        : `${this.config.gameName}云端和本机都暂无存档，请先开始游戏并保存。`;
    this.downloadButton.disabled = !this.cloudSave;
    this.uploadButton.disabled = !this.engine.hasSave();
  }
}
